import React from "react"
import { ScrollView, Text, TextStyle, View, ViewStyle } from "react-native"

import { ListItem } from "@rn-vui/themed"

import { useChat } from "src/context/ChatContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useCloseCurrentConversation } from "src/queries/useConversation"
import { useLLMAvailable } from "src/queries/useLLMAvailable"
import { ThemedStyle } from "src/theme"
import { APP_EVENTS, appEvents } from "src/utils/events"
import { backToRoot } from "src/utils/routerHelpers"

export function LLMListMenu() {
  const { data: llms, isLoading } = useLLMAvailable()
  const { mutateAsync: closeConversationAsync } = useCloseCurrentConversation()
  const { themed } = useAppTheme()

  const { startNewConversation, currentLLMId, setCurrentLLMId } = useChat()

  const isLLMSelected = React.useCallback((llmId: string) => llmId === currentLLMId, [currentLLMId])
  const isDefaultSelected = !currentLLMId

  if (isLoading) {
    return (
      <ScrollView style={themed($container)}>
        <Text style={themed($loadingText)}>Loading LLMs...</Text>
      </ScrollView>
    )
  }

  return (
    <View style={themed($container)}>
      <ScrollView>
        <ListItem
          key="default"
          bottomDivider
          containerStyle={[themed($listItem), isDefaultSelected && themed($selectedListItem)]}
          onPress={async () => {
            setCurrentLLMId(undefined)
            await closeConversationAsync()
            startNewConversation(null, undefined)
            backToRoot()
            appEvents.emit(APP_EVENTS.CLOSE_BOTTOM_MENU)
          }}
        >
          <ListItem.Content>
            <ListItem.Title
              style={[themed($sectionTitle), isDefaultSelected && themed($selectedTitle)]}
            >
              Default (Backend Selected)
            </ListItem.Title>
          </ListItem.Content>
          {isDefaultSelected && <ListItem.Chevron color={themed($selectedChevron).color} />}
        </ListItem>
        {llms?.map((llm) => {
          const isSelected = isLLMSelected(llm.id)

          return (
            <ListItem
              key={llm.id}
              bottomDivider
              containerStyle={[themed($listItem), isSelected && themed($selectedListItem)]}
              onPress={async () => {
                setCurrentLLMId(llm.id)
                await closeConversationAsync()
                startNewConversation(null, llm.id)
                backToRoot()
                appEvents.emit(APP_EVENTS.CLOSE_BOTTOM_MENU)
              }}
            >
              <ListItem.Content>
                <ListItem.Title
                  style={[themed($sectionTitle), isSelected && themed($selectedTitle)]}
                >
                  {llm.type}
                </ListItem.Title>
              </ListItem.Content>
              {isSelected && <ListItem.Chevron color={themed($selectedChevron).color} />}
            </ListItem>
          )
        })}
      </ScrollView>
    </View>
  )
}

const $container: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  borderRadius: spacing.sm,
  backgroundColor: colors.background,
  overflow: "hidden",
})

const $sectionTitle: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.text,
  fontFamily: typography.fonts.figtree.normal,
  fontSize: 16,
})

const $listItem: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  padding: spacing.lg,
  borderTopWidth: 0,
  borderBottomWidth: 1,
  borderBottomColor: colors.palette.skListItemCategoryBackground,
  backgroundColor: colors.palette.skListItemBackground,
})

const $selectedListItem: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.palette.skListItemCategoryBackground,
})

const $selectedTitle: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.primary,
  fontWeight: "bold",
})

const $selectedChevron: ThemedStyle<{ color: string }> = ({ colors }) => ({
  color: colors.primary,
})

const $loadingText: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.text,
})
